import { ProcessTableStateType, useProcessTableState } from "../../../context/ProcessTableContext";
import { GroupBy, GroupByCategory, ProcessFilterZodArray } from "@avenueops/shared-types";
import { Button } from "@avenueops/web-components/";
import { ArrowPathIcon, PlusIcon, Square3Stack3DIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ProcessTimeFrame, ProcessView } from "@prisma/client";
import { isEqual } from "date-fns";
import _ from "lodash";
import React from "react";

interface Props {
  originalSignalView: ProcessView;
  setNewModalVisible: (_: boolean) => void;
  setUpdateModalVisible: (_: boolean) => void;
  setDeleteModalVisible: (_: boolean) => void;
}

/**
 * Update an existing view, reset the view to its saved state, save as a new view, or delete the view.
 */
export default function ExistingSignalViewSection({ originalSignalView, setNewModalVisible, setUpdateModalVisible, setDeleteModalVisible }: Props) {
  const [processTableState, setProcessTableState] = useProcessTableState();
  const { filters, groupBy, timeFrame, timeFrameStartIncl, timeFrameEndExcl } = processTableState;

  return (
    <div className="flex flex-col gap-y-2 w-full">
      <div className="flex flex-row gap-x-2">
        <Button
          disabled={matchesOriginalSignalView(originalSignalView, filters ?? [], timeFrame, timeFrameStartIncl, timeFrameEndExcl, groupBy)}
          onClick={() => resetToOriginalSignalView(originalSignalView, processTableState, setProcessTableState)}
          type="SECONDARY"
          full
        >
          <ArrowPathIcon className="h-4 w-4 mr-1" />
          Reset
        </Button>
        <Button onClick={() => setUpdateModalVisible(true)} full>
          <Square3Stack3DIcon className="h-4 w-4 mr-1" />
          Save
        </Button>
      </div>
      <Button onClick={() => setNewModalVisible(true)} type="SECONDARY">
        <PlusIcon className="h-4 w-4 mr-1" />
        Save as New
      </Button>
      <Button onClick={() => setDeleteModalVisible(true)} type="DANGER_SECONDARY">
        <TrashIcon className="h-4 w-4 mr-1" />
        Delete
      </Button>
    </div>
  );
}

/**
 * Check if the current filters and group by values match those of the original view.
 */
function matchesOriginalSignalView(
  originalSignalView: ProcessView,
  filters: ProcessFilterZodArray,
  timeFrame: ProcessTimeFrame,
  timeFrameStartIncl?: Date,
  timeFrameEndExcl?: Date,
  groupBy?: GroupByCategory
) {
  const filtersIsEqual = _.isEqual(filters, originalSignalView.filters as ProcessFilterZodArray);
  const groupByIsEqual = (!originalSignalView.groupBy && !groupBy) || (originalSignalView.groupBy as GroupByCategory) === groupBy;
  const timeFrameIsEqual = originalSignalView.timeFrame === timeFrame;
  const timeFrameStartInclIsEqual = isEqual(new Date(timeFrameStartIncl ?? 0), new Date(originalSignalView.timeFrameStartIncl ?? 0));
  const timeFrameEndExclIsEqual = isEqual(new Date(timeFrameEndExcl ?? 0), new Date(originalSignalView.timeFrameEndExcl ?? 0));
  return filtersIsEqual && groupByIsEqual && timeFrameIsEqual && timeFrameStartInclIsEqual && timeFrameEndExclIsEqual;
}

/**
 * Reset the process table state to match that of the original view.
 */
function resetToOriginalSignalView(
  originalSignalView: ProcessView,
  processTableState: ProcessTableStateType,
  setProcessTableState: (_: ProcessTableStateType) => void
) {
  const groupBy = originalSignalView.groupBy ? (originalSignalView.groupBy as GroupBy) : undefined;
  const filters = originalSignalView.filters as ProcessFilterZodArray;
  const timeFrame = originalSignalView.timeFrame as ProcessTimeFrame;
  const timeFrameStartIncl = originalSignalView.timeFrameStartIncl ? new Date(originalSignalView.timeFrameStartIncl) : undefined;
  const timeFrameEndExcl = originalSignalView.timeFrameEndExcl ? new Date(originalSignalView.timeFrameEndExcl) : undefined;

  setProcessTableState({ ...processTableState, groupBy, filters, timeFrame, timeFrameStartIncl, timeFrameEndExcl });
}
